import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Send, X } from 'lucide-react';
import { Button } from 'components/Elements';
import { ReactSelectField } from 'components/Form/ReactSelectField';
import { useSendNotification } from '../api/sendNotification';
import { useDebounce } from 'hooks/useDebounce';
import { useSearchCustomers } from 'features/users';
import { InputField } from 'components/Form';

export const SendNotificationNavigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 500);

  const [selectedUser, setSelectedUser] = useState(null);
  const [type, setType] = useState({ value: 'primary', label: 'Primary' });
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const { data: searchResults } = useSearchCustomers({
    text: debouncedSearch,
  });

  const sendNotification = useSendNotification();
  const [isSending, setIsSending] = useState(false);

  const handleSend = async () => {
    try {
      setIsSending(true);
      await sendNotification.mutateAsync({
        id: selectedUser.value,
        title,
        body,
        type: type.value,
      });
      setIsOpen(false);
      setSelectedUser(null);
      setType({ value: 'primary', label: 'Primary' });
      setTitle('');
      setBody('');
    } catch (err) {
      console.error('Notification failed to send', err);
    } finally {
      setIsSending(false);
    }
  };

  const userOptions =
    searchResults?.users?.map((user) => ({
      label: `${user.firstName} ${user.lastName} (${user.email})`,
      value: user.id,
    })) || [];

  const notificationTypeOptions = [
    { value: 'transaction', label: 'Transaction' },
    { value: 'primary', label: 'Primary' },
    { value: 'system', label: 'System' },
    { value: 'updates', label: 'Updates' },
  ];

  return (
    <>
      <Button
        variant="glass"
        onClick={() => setIsOpen(true)}
        endIcon={<Send className="text-white" size={14} />}
      >
        Send Notification
      </Button>

      <AnimatePresence>
        {isOpen && (
          <Dialog
            as="div"
            open={isOpen}
            onClose={() => setIsOpen(false)}
            className="relative z-50"
          >
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Dialog.Panel
                as={motion.div}
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 50, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="w-full max-w-lg rounded-2xl bg-[linear-gradient(to_bottom,_#2F6ACB,_#4455D0,_#4B3CBF)] dark:bg-[linear-gradient(to_bottom,_#0F2027,_#203A43,_#2C5364)] p-6 shadow-xl text-white"
              >
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title className="text-lg font-semibold">
                    Send Notification
                  </Dialog.Title>

                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 hover:bg-white/10 rounded-md"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: {},
                    visible: { transition: { staggerChildren: 0.1 } },
                  }}
                  className="space-y-4"
                >
                  <motion.div
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      visible: { opacity: 1, y: 0 },
                    }}
                  >
                    <div className="text-sm text-white/50">
                      Keep your users informed and engaged with real-time
                      notifications tailored to their activity, status, or
                      preferences.
                    </div>
                  </motion.div>
                  <motion.div
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      visible: { opacity: 1, y: 0 },
                    }}
                  >
                    <ReactSelectField
                      label="Search User"
                      name="user"
                      options={userOptions}
                      setValue={() => {}}
                      onChange={(val) => setSelectedUser(val)}
                      onInputChange={(input) => setSearchText(input)}
                    />
                  </motion.div>

                  <motion.div
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      visible: { opacity: 1, y: 0 },
                    }}
                  >
                    <ReactSelectField
                      label="Notification Type"
                      name="type"
                      options={notificationTypeOptions}
                      setValue={() => {}}
                      onChange={(val) => setType(val)}
                      defaultValue={notificationTypeOptions[1]}
                    />
                  </motion.div>

                  <motion.div
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      visible: { opacity: 1, y: 0 },
                    }}
                  >
                    <InputField
                      label="Title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Notification title"
                    />
                  </motion.div>

                  <motion.div
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      visible: { opacity: 1, y: 0 },
                    }}
                  >
                    <InputField
                      label="Body"
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Notification body"
                    />
                  </motion.div>
                </motion.div>

                <motion.div
                  className="mt-6 flex justify-end"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="glass"
                    onClick={handleSend}
                    isLoading={isSending}
                    className="w-full"
                    disabled={!selectedUser || !title || !body}
                    endIcon={<Send className="text-white" size={14} />}
                  >
                    Send
                  </Button>
                </motion.div>
              </Dialog.Panel>
            </div>
          </Dialog>
        )}
      </AnimatePresence>
    </>
  );
};
